/**
 * Reference count for the scroll lock.
 *
 * The panel stacks overlapping layers, so a per-layer lock would hand scrolling back
 * when the topmost closes while one underneath is still open. Scrolling is locked on the
 * first acquisition and returned only when the last layer closes, and `release` is
 * idempotent because a re-running effect would otherwise decrement twice.
 */

export type ScrollLockTarget = {
  lock: () => void;
  unlock: () => void;
};

export type ScrollLock = {
  /** Locks scrolling and returns the release for this layer. */
  acquire: () => () => void;
  /** Releases every layer at once, for screen unmount. */
  releaseAll: () => void;
  readonly activeLocks: number;
};

export function createScrollLock(target: ScrollLockTarget): ScrollLock {
  let count = 0;
  /**
   * `releaseAll` opens a new generation.
   *
   * Releases issued before it still exist, because unmount calls `releaseAll` and then
   * every effect runs its own cleanup. Without the generation those late releases would
   * drive the count negative and the next acquisition would never call `lock()`.
   */
  let generation = 0;

  return {
    acquire() {
      const issuedAt = generation;

      count += 1;
      if (count === 1) target.lock();

      let released = false;

      return () => {
        if (released) return;
        released = true;

        if (issuedAt !== generation) return;

        count -= 1;
        if (count === 0) target.unlock();
      };
    },

    releaseAll() {
      generation += 1;
      if (count === 0) return;

      count = 0;
      target.unlock();
    },

    get activeLocks() {
      return count;
    },
  };
}
